import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AppShell } from './components/shell/AppShell.jsx';
import { Spinner } from './components/ui/Spinner.jsx';
import ChatPage from './views/ChatPage';
import MissionsPage from './views/MissionsPage';
import KnowledgePage from './views/KnowledgePage';
import OnboardingPage from './views/OnboardingPage';
import SettingsPage from './views/SettingsPage';
import LoginPage from './views/LoginPage';
import SetupPage from './views/SetupPage';
import AdminPage from './views/AdminPage';
import { LearningProvider } from './context/LearningContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConversationProvider } from './context/ConversationContext';
import { PlatformProvider } from './context/PlatformContext';

/**
 * Guards that redirect to /setup (uninitialized) or /login (not authenticated).
 * Checks /api/setup/status once on mount.
 */
function AppGuard({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [setupChecked, setSetupChecked] = useState(false);
  const [adminExists, setAdminExists] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    fetch('/api/setup/status')
      .then(r => r.json())
      .then(data => {
        setAdminExists(!!data.adminExists);
        setInitialized(!!data.initialized);
        setSetupChecked(true);
      })
      .catch(() => setSetupChecked(true));
  }, []);

  const publicRoutes = ['/setup', '/login'];
  const isPublic = publicRoutes.some(p => location.pathname.startsWith(p));

  if (!setupChecked || authLoading) return (
    <div className="etch-shell-boot">
      <Spinner size="lg" label={t('states.loading')} />
    </div>
  );

  // No admin yet → must go through setup
  if (!adminExists && !isPublic) return <Navigate to="/setup" replace />;
  // Setup fully complete → /setup is closed; partial setup (adminExists but !initialized) stays open for delete-&-restart
  if (initialized && location.pathname.startsWith('/setup')) return <Navigate to={user ? '/' : '/login'} replace />;
  // Admin exists + setup done + not logged in → login
  if (adminExists && !user && !isPublic) return <Navigate to="/login" replace />;

  return children;
}

// AdminGuard is intentionally permissive — the page itself filters which
// tabs are visible based on role. The backend remains the source of truth
// for authz on the admin-only API endpoints.
function AdminGuard({ children }) {
  return children;
}

/**
 * Compatibility redirect that keeps the sub-path: `/learn/session/7` lands on
 * `/missions/session/7` rather than dumping the user on the index (spec §8.1).
 */
function PathRedirect({ from, to }) {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.startsWith(from) ? pathname.slice(from.length) : '';
  return <Navigate to={`${to}${rest}${search}${hash}`} replace />;
}

function AppRoutes() {
  return (
    <AppGuard>
      <Routes>
        {/* Public routes — no app chrome (spec §10.1) */}
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Authenticated routes — inside the Etch app frame (spec §8.1) */}
        <Route path="/" element={<AppShell />}>
          <Route index element={<ChatPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="missions/*" element={<MissionsPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="knowledge/*" element={<KnowledgePage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route
            path="admin/*"
            element={
              <AdminGuard>
                <AdminPage />
              </AdminGuard>
            }
          />

          {/* Compatibility redirects for the pre-Etch information architecture */}
          <Route path="learn" element={<Navigate to="/missions" replace />} />
          <Route path="learn/*" element={<PathRedirect from="/learn" to="/missions" />} />
          <Route path="ide" element={<Navigate to="/knowledge" replace />} />
          <Route path="ide/*" element={<PathRedirect from="/ide" to="/knowledge" />} />
          <Route path="qa" element={<Navigate to="/chat" replace />} />

          {/* Unknown path inside the frame → the start page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AppGuard>
  );
}

function App() {
  return (
    <ErrorBoundary showDetails={import.meta.env.DEV}>
      <AuthProvider>
        <PlatformProvider>
          <LearningProvider>
            {/* Inside AuthProvider on purpose: a conversation belongs to an
                account (migration 010), so the history has to reload when the
                signed-in user changes. */}
            <ConversationProvider>
              <AppRoutes />
            </ConversationProvider>
          </LearningProvider>
        </PlatformProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
